import type { UPSSizingResponse } from "../services/upsSizing";
import {
  NOT_ESTABLISHED_WARNING_CODES,
  type UPSGoverningBasis,
  type UPSRedundancyMode,
} from "../services/upsSizingContract";
import { EMPTY_QUANTITY, formatQuantity } from "../utils/formatQuantity";
import {
  REDUNDANCY_LABELS as GENERATOR_REDUNDANCY_LABELS,
  STATUS_LABELS as GENERATOR_STATUS_LABELS,
  STATUS_TONE as GENERATOR_STATUS_TONE,
} from "./GeneratorResultSummary";
import { StatusIcon } from "./StatusIcon";
import { bannerTone } from "./statusTone";

// The same words and the same colour as the Transformer and DG summaries: an
// unestablished input is a review the engineer owes, not a design failure.
// No adequate rating is a failure - there is nothing to build with. The maps
// are the DG summary's, typed here against the UPS enums so a value only the
// UPS gains cannot pass without words and a colour.
const STATUS_LABELS: Record<UPSSizingResponse["status"], string> = GENERATOR_STATUS_LABELS;

const STATUS_TONE: Record<UPSSizingResponse["status"], "pass" | "fail" | "warning"> =
  GENERATOR_STATUS_TONE;

const REDUNDANCY_LABELS: Record<UPSRedundancyMode, string> = GENERATOR_REDUNDANCY_LABELS;

// The engine states which of the two rating limits governed (A18 (c)); the
// summary names it after the requirement figure.
const GOVERNING_BASIS_LABELS: Record<UPSGoverningBasis, string> = {
  KVA: "kVA limit",
  KW: "kW limit",
};

type UPSResultSummaryProps = {
  result: UPSSizingResponse;
};

// The decisive values first (Master Prompt v2.1 section 19): sizing state, the
// module rating chosen from the schedule, the requirement that governed it, the
// module rating it needed after derating, the module arrangement, both loading
// figures and the battery energy the runtime asks for.
// Display rule A12: four significant figures on screen, the exact engine
// decimal in the tooltip (title attribute). The selected rating and the runtime
// are values the engineer supplied, so they are shown exactly as they were sent.
export function UPSResultSummary({ result }: UPSResultSummaryProps) {
  const tone = STATUS_TONE[result.status];
  const warningCount = result.warnings.length;
  const notEstablishedCount = result.warnings.filter((warning) =>
    (NOT_ESTABLISHED_WARNING_CODES as readonly string[]).includes(warning.code),
  ).length;

  const governing = formatQuantity(result.governing_requirement_kva, "kVA");
  const requiredRating = formatQuantity(result.required_unit_rating_kva, "kVA");
  const loadingKw = formatQuantity(result.loading_kw_percent, "%");
  const loadingKva = formatQuantity(result.loading_kva_percent, "%");
  const batteryEnergy = formatQuantity(result.battery_dc_energy_kwh, "kWh");
  const hasLoading = loadingKw.exact !== null && loadingKva.exact !== null;

  return (
    <section aria-label="Result summary" data-summary-tone={tone}>
      <header>
        <p data-summary-status={result.status}>
          <StatusIcon tone={bannerTone(result.status, STATUS_TONE)} size={20} />
          {STATUS_LABELS[result.status]}
        </p>
        <p>
          Study <strong>{result.code}</strong> · {result.name} ·{" "}
          {result.jurisdiction_profile} profile
        </p>
        {result.selected_unit_rating_kva === null ? (
          <p data-no-rating="true">
            No module rating in the schedule covers {requiredRating.display}.
          </p>
        ) : null}
      </header>
      <dl data-summary-strip>
        <div>
          <dt>Selected module rating</dt>
          {/* A catalogue value from the project design basis: shown as sent, so
              the engineer reads back the rating that is actually orderable. */}
          <dd data-summary-field="selected-rating" data-summary-value="text">
            {result.selected_unit_rating_kva === null
              ? "No module rating fits"
              : `${result.total_modules} × ${result.selected_unit_rating_kva} kVA`}
          </dd>
        </div>
        <div>
          <dt>Governing requirement</dt>
          <dd data-summary-field="governing-requirement" data-summary-value="text" title={governing.exact ?? undefined}>
            {governing.display}{" "}
            <span data-governing-basis={result.governing_basis}>
              ({GOVERNING_BASIS_LABELS[result.governing_basis]})
            </span>
          </dd>
        </div>
        <div>
          <dt>Required module rating (after derating)</dt>
          <dd data-summary-field="required-module-rating" title={requiredRating.exact ?? undefined}>
            {requiredRating.display}
          </dd>
        </div>
        <div>
          <dt>Modules</dt>
          {/* Counts, not engineering quantities: shown as the integers they are. */}
          <dd data-summary-field="modules" data-summary-value="text">
            {result.duty_modules} duty + {result.redundant_modules} redundant ·{" "}
            {REDUNDANCY_LABELS[result.redundancy_mode]}
          </dd>
        </div>
        <div>
          <dt>Loading</dt>
          {/* Both limits (A18 (c)): design kW over the derated kW capacity and
              load kVA over the derated kVA capacity. Empty when no rating fits. */}
          <dd
            data-summary-field="loading" data-summary-value="text"
            title={hasLoading ? `${loadingKw.exact} kW · ${loadingKva.exact} kVA` : undefined}
          >
            {hasLoading ? `${loadingKw.display} kW · ${loadingKva.display} kVA` : EMPTY_QUANTITY}
          </dd>
        </div>
        <div>
          <dt>Battery DC energy</dt>
          {/* The runtime is the engineer's input (A18 (b)): shown as sent. */}
          <dd data-summary-field="battery-energy" data-summary-value="text" title={batteryEnergy.exact ?? undefined}>
            {batteryEnergy.display} ({result.required_runtime_minutes} min)
          </dd>
        </div>
        {notEstablishedCount > 0 ? (
          <div>
            <dt>Inputs not established</dt>
            <dd data-summary-field="not-established">{notEstablishedCount}</dd>
          </div>
        ) : null}
        <div>
          <dt>Warnings</dt>
          <dd data-summary-warnings={warningCount}>{warningCount}</dd>
        </div>
      </dl>
    </section>
  );
}

export { GOVERNING_BASIS_LABELS, REDUNDANCY_LABELS, STATUS_LABELS, STATUS_TONE };
